// types
import { createSlice } from '@reduxjs/toolkit';
import { Simulation } from 'models/Simulation';
import UpdateParam from 'models/requests/UpdateParam';
import ParamsService from 'services/ParamsService';
import SimulationService from 'services/SimulationService';
import { dispatch } from 'store';

// initial state
const delay = 1000;
interface SimulationState {
    error: null;
    isLoading: boolean;
    isInitialized: boolean;
    isActive: boolean;
    openAlert: boolean;
    openAccept: boolean;
    openDelete: boolean;
    openEdit: boolean;
    openCreateSimulationForm: boolean;
    simulationId: string | null;
    simulations: Simulation[];
}
const initialState: SimulationState = {
    error: null,
    isLoading: false,
    isInitialized: false,
    isActive: true,
    openAlert: false,
    openAccept: false,
    openDelete: false,
    openEdit: false,
    openCreateSimulationForm: false,
    simulationId: null,
    simulations: []
};

// ==============================|| SLICE - SIMULATION ||============================== //

const simulation = createSlice({
    name: 'simulation',
    initialState,
    reducers: {
        hasError(state, action) {
            state.error = action.payload;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        initializeSimulation(state, action) {
            state.isInitialized = action.payload;
        },
        setIsActive(state, action) {
            state.isActive = action.payload;
        },
        openSimulationAlert(state, action) {
            state.openAlert = action.payload;
        },
        openAcceptAlert(state, action) {
            state.openAccept = action.payload;
        },
        openEditAlert(state, action) {
            state.openEdit = action.payload;
        },
        openDeleteAlert(state, action) {
            state.openDelete = action.payload;
        },
        openCreateSimulationForm(state, action) {
            state.openAlert = false;
            state.openCreateSimulationForm = action.payload;
        },
        changeSimulation(state, action) {
            state.simulationId = action.payload;
        },
        setSimulations(state, action) {
            state.simulations = action.payload;
        }
    }
});

export default simulation.reducer;

const simulationService = new SimulationService();

// ----------------------------[ SIMULATION ]----------------------------
export const onInitializeSimulation = (simulationId?: string | null) => {
    dispatch(simulation.actions.initializeSimulation(true));
    if (simulationId) {
        dispatch(simulation.actions.changeSimulation(simulationId));
        return dispatch(simulation.actions.setIsActive(true));
    }
    return dispatch(simulation.actions.setIsActive(false));
};

export const onToggleSimulation = (tenantId: string, userName: string, state: boolean, simulationId?: string | null) => async () => {
    dispatch(simulation.actions.setIsLoading(true));
    dispatch(simulation.actions.changeSimulation(simulationId));
    setTimeout(async () => {
        try {
            dispatch(simulation.actions.setIsActive(state));
            const res = await simulationService.changeSimulation(tenantId, userName, simulationId);
            dispatch(simulation.actions.setIsLoading(false));
            return res;
        } catch (error) {
            dispatch(simulation.actions.setIsLoading(false));
            return false;
        }
    }, delay);
};

export const onChangeSimulation = (tenantId: string, userName: string, simulationId: string) => async () => {
    dispatch(simulation.actions.setIsLoading(true));
    dispatch(simulation.actions.changeSimulation(simulationId));
    setTimeout(async () => {
        try {
            const res = await simulationService.changeSimulation(tenantId, userName, simulationId);
            dispatch(simulation.actions.setIsLoading(false));
            return res;
        } catch (error) {
            dispatch(simulation.actions.setIsLoading(false));
            return false;
        }
    }, delay);
};

export const onFetchSimulations = (tenantId: string) => async () => {
    try {
        if (!tenantId) return false;
        const res = await simulationService.getSimulationsItems(tenantId);
        dispatch(simulation.actions.setSimulations(res));
        if (res.length === 0) {
            dispatch(simulation.actions.setIsActive(false));
            dispatch(simulation.actions.changeSimulation(null));
            dispatch(simulation.actions.initializeSimulation(false));
        }
        return true;
    } catch (error) {
        return false;
    }
};

// ----------------------------[ SIMULATION ALERT ]----------------------------
export const onOpenSimulationAlert = (state: boolean) =>
    // if (paramToUpdate) dispatch(simulation.actions.setParamToUpdate(null));
    // else dispatch(simulation.actions.setParamToUpdate(paramToUpdate));
    dispatch(simulation.actions.openSimulationAlert(state));

// ----------------------------[ CREATE SIMULATION ]----------------------------
export const onOpenCreateSimulationForm = (open: boolean) => dispatch(simulation.actions.openCreateSimulationForm(open));
export const onCreateSimulation = (tenantId: string, userId: string, userName: string, newSimulation: Simulation) => async () => {
    try {
        dispatch(simulation.actions.setIsLoading(true));
        const newSimulationId = await simulationService.upsertSimulation(tenantId, userId, userName, newSimulation);
        if (newSimulationId) {
            dispatch(simulation.actions.initializeSimulation(true));
            dispatch(simulation.actions.setIsActive(true));
            dispatch(simulation.actions.changeSimulation(newSimulationId));
            // if (paramToUpdate) {
            //     const newParamToUpdate: UpdateParam = { ...paramToUpdate, simulazioneId: newSimulationId };
            //     return await paramsService.updateValue(newParamToUpdate);
            // }
            dispatch(simulation.actions.setIsLoading(false));
            return true;
        }
        dispatch(simulation.actions.setIsLoading(false));
        return false;
    } catch (error) {
        dispatch(simulation.actions.setIsLoading(false));
        return false;
    }
};

// ----------------------------[ EDIT SIMULATION ]----------------------------
export const onOpenEditAlert = (open: boolean) => dispatch(simulation.actions.openEditAlert(open));
export const onEditSimulation = (tenantId: string, userId: string, userName: string, newSimulation: Simulation) => async () => {
    try {
        const newSimulationId = await simulationService.upsertSimulation(tenantId, userId, userName, newSimulation);
        return !!newSimulationId;
    } catch (error) {
        return false;
    }
};

// ----------------------------[ DELETE SIMULATION ]----------------------------

export const onOpenDeleteAlert = (open: boolean) => dispatch(simulation.actions.openDeleteAlert(open));
export const onDeleteSimulation = (userId: string, username: string, simulationId: string) => async () => {
    dispatch(simulation.actions.setIsLoading(true));
    dispatch(simulation.actions.setIsActive(false));
    dispatch(simulation.actions.changeSimulation(null));

    let res = false;

    try {
        res = await new Promise<boolean>((resolve, reject) => {
            setTimeout(async () => {
                try {
                    const res = await simulationService.deleteSimulation(userId, username, simulationId);
                    resolve(res);
                } catch (error) {
                    console.log(error);
                    reject(error);
                }
            }, delay);
        });
        dispatch(simulation.actions.setIsLoading(false));
    } catch (error) {
        dispatch(simulation.actions.setIsLoading(false));
    }

    return res;
};

// ----------------------------[ ACCEPT SIMULATION ]----------------------------

export const onOpenAcceptAlert = (state: boolean) => dispatch(simulation.actions.openAcceptAlert(state));
export const onAcceptSimulation = (tenantId: string, userId: string, username: string, simulationId: string) => async () => {
    dispatch(simulation.actions.setIsLoading(true));
    dispatch(simulation.actions.setIsActive(false));
    dispatch(simulation.actions.changeSimulation(null));

    let res = false;

    try {
        res = await new Promise<boolean>((resolve, reject) => {
            setTimeout(async () => {
                try {
                    const res = await simulationService.acceptSimulation(tenantId, userId, username, simulationId);
                    resolve(res);
                } catch (error) {
                    console.log(error);
                    reject(error);
                }
            }, delay);
        });
        dispatch(simulation.actions.setIsLoading(false));
    } catch (error) {
        dispatch(simulation.actions.setIsLoading(false));
    }

    return res;
};
